import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import {
  convertValueToString,
  listTextDirectionCoordinates,
  replacePathParams,
} from 'helpers/funcs';
import { RootState } from 'types/RootState';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getListImplementByEmployeeAndConsultant,
  setIsShowListEvent,
} from '../../../store/implements/implementsSlice';
import {
  clearAllDataOmoteura,
  getListFeedbacksCom,
  getListOmoteura,
  setListOmoteura,
  setOmoteureEnergyDetail,
} from '../../../store/omoteura/omoteuraSlice';
import { getUserFromLocalStorage } from '../../../helpers/localStorage';
import { EPath } from '../../routes/routesConfig';
import { getListFeedback } from 'store/consultantInterview/consultantInterview';
import { implementApi } from 'api/implementApi';
import { TImplement } from 'types/clientManagement/clientManagement';

export const useServices = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { listOmoteura, infoEmployee, infoInterview, loading } = useSelector(
    (state: RootState) => state.omoteura,
  );
  const [selectedLabel, setSelectedLabel] = useState(0);
  const { employeeId, eventId } = useParams();
  const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
  const [temStep, setTemStep] = useState<number>(0);
  const [step, setStep] = useState<number>(1);
  const [eventSelected, setEventSelected] = useState<string>(convertValueToString(eventId));
  const location = useLocation();
  const [eventSelectedConsultant, setEventSelectedConsultant] = useState<string>(
    convertValueToString(location.pathname.split('/')[4]),
  );
  const [listImplementByEmployeeAll, setListImplementByEmployeeAll] = useState<TImplement[]>([]);
  const normalizeImplementEvents = (payload: any): TImplement[] => {
    if (Array.isArray(payload)) {
      return payload;
    }
    const merged: TImplement[] = [
      ...(Array.isArray(payload?.events) ? payload.events : []),
      ...(Array.isArray(payload?.regularEvents) ? payload.regularEvents : []),
      ...(Array.isArray(payload?.additionalEvents) ? payload.additionalEvents : []),
      ...(Array.isArray(payload?.employeeProgresses) ? payload.employeeProgresses : []),
      ...(Array.isArray(payload?.additionalInterviews) ? payload.additionalInterviews : []),
      ...(Array.isArray(payload?.implements) ? payload.implements : []),
    ];
    const uniqueMap = new Map<string, TImplement>();
    merged.forEach((item) => {
      const key = String(item?._id ?? item?.eventId ?? '');
      if (key) {
        uniqueMap.set(key, item);
      }
    });
    return Array.from(uniqueMap.values());
  };
  const employeeProgressId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('employeeProgressId') ?? '';
  }, [location.search]);

  const currentUser = useMemo(() => getUserFromLocalStorage(), []);
  const eventListForSelect = useMemo(() => listImplementByEmployeeAll, [listImplementByEmployeeAll]);
  const selectedEventOptionValue = useMemo(() => {
    const byEvent = eventListForSelect.filter(
      (item) =>
        convertValueToString(item?._id ?? item?.eventId) === convertValueToString(eventSelected),
    );
    if (byEvent.length === 0) {
      return `${convertValueToString(eventSelected)}::${convertValueToString(employeeProgressId)}`;
    }
    const exactMatch =
      byEvent.find(
        (item) =>
          convertValueToString((item as any)?.employeeProgressId) ===
          convertValueToString(employeeProgressId),
      ) ?? byEvent[0];
    return `${convertValueToString(exactMatch?._id ?? exactMatch?.eventId)}::${convertValueToString(
      (exactMatch as any)?.employeeProgressId,
    )}`;
  }, [eventListForSelect, eventSelected, employeeProgressId]);

  useEffect(() => {
    eventId && setEventSelected(eventId);
  }, [eventId]);


  useEffect(() => {
    dispatch(setIsShowListEvent(true));
  }, []);

  useEffect(() => {
    if (employeeId && currentUser) {
      const fetchAllEventsByEmployee = async () => {
        try {
          const response = await implementApi.getListEventByEmployeeAll(convertValueToString(employeeId));
          setListImplementByEmployeeAll(normalizeImplementEvents(response?.data?.data));
        } catch (error) {
          setListImplementByEmployeeAll([]);
        }
      };

      fetchAllEventsByEmployee();
      if (currentUser.consultant) {
        dispatch(
          getListImplementByEmployeeAndConsultant({
            employeeId,
            consultantId: currentUser.consultant._id,
          }),
        );
      }
    }
  }, []);

  useEffect(() => {
    if (eventId && location.pathname.includes('/implements/omoteura-diagnosis-result')) {
      dispatch(
        setListOmoteura({
          type: 'getList',
          omoteuras: [],
          total: 0,
          page: 1,
          perPage: 9999,
          infoEmployee: {},
          infoInterview: {},
        }),
      );
      dispatch(setOmoteureEnergyDetail([]));
      dispatch(
        getListOmoteura({
          employeeId: employeeId,
          eventId: convertValueToString(eventSelected),
          employeeProgressId,
        }),
      );

      if (currentUser.com) {
        dispatch(getListFeedbacksCom({ employeeId, eventId: eventSelected, employeeProgressId }));
      }
    } else if (eventId && currentUser.consultant) {
      dispatch(
        getListFeedback({
          consultantId: currentUser.consultant._id,
          employeeId: employeeId ?? '',
          eventId: eventSelected,
        }),
      );
    }
  }, [
    eventSelected,
    employeeProgressId,
    employeeId,
    eventId,
    location.pathname,
    currentUser?.com,
    currentUser?.consultant?._id,
  ]);

  useEffect(() => {
    setDirection({
      ...direction,
      value: Number(listOmoteura[listOmoteura.length - 1]?.balance1),
      shortDescription: listOmoteura[listOmoteura.length - 1]?.['short-description'][0],
      pointExplanation: listOmoteura[listOmoteura.length - 1]?.descriptions[0],
    });
  }, [listOmoteura]);

  const [direction, setDirection] = useState({
    text: listTextDirectionCoordinates[0],
    value: Number(listOmoteura[0]?.balance1),
    shortDescription: '',
    pointExplanation: listOmoteura[0]?.descriptions[0],
    index: 0,
  });
  const handleChangeDirection = (index: number) => {
    setDirection({
      text: listTextDirectionCoordinates[index],
      value: Number(listOmoteura[listOmoteura.length - 1]?.[`balance${index + 1}`]),
      shortDescription: listOmoteura[listOmoteura.length - 1]?.['short-description'][index],
      pointExplanation: listOmoteura[listOmoteura.length - 1]?.descriptions[index],
      index: index,
    });
  };

  const handleSetEventSelected = (combinedValue: string) => {
    const [selectedEventId = '', selectedProgressId = ''] = convertValueToString(combinedValue).split(
      '::',
    );
    setEventSelected(selectedEventId);
    const selectedEvent = eventListForSelect.find(
      (item) =>
        convertValueToString(item?._id ?? item?.eventId) === convertValueToString(selectedEventId) &&
        (!selectedProgressId ||
          convertValueToString((item as any)?.employeeProgressId) ===
            convertValueToString(selectedProgressId)),
    );
    const selectedEventType = convertValueToString(
      (selectedEvent as any)?.interviewType ??
        (selectedEvent as any)?.eventType ??
        (selectedEvent as any)?.type ??
        (selectedEvent as any)?.category,
    ).toLowerCase();
    const selectedEmployeeProgressId = convertValueToString(
      selectedProgressId || (selectedEvent as any)?.employeeProgressId || employeeProgressId,
    );
    const destinationPath =
      selectedEventType === 'additional' ? EPath.additionalOmoteuraDiagnosi : EPath.omoteuraDiagnosi;
    const destinationUrl = selectedEmployeeProgressId
      ? `${replacePathParams(destinationPath, {
          employeeId: convertValueToString(employeeId),
          eventId: convertValueToString(selectedEventId),
        })}?employeeProgressId=${encodeURIComponent(selectedEmployeeProgressId)}`
      : replacePathParams(destinationPath, {
          employeeId: convertValueToString(employeeId),
          eventId: convertValueToString(selectedEventId),
        });
    navigate(
      destinationUrl,
    );
  };

  useEffect(() => {
    return () => {
      dispatch(clearAllDataOmoteura());
    };
  }, []);

  const answerCurrent = useMemo(() => {
    return listOmoteura.find((item) => item.eventId === eventId)?.answer;
  }, [eventId, listOmoteura]);

  return {
    t,
    handleChangeDirection,
    listOmoteura,
    direction,
    infoEmployee,
    selectedLabel,
    setSelectedLabel,
    listImplementByEmployee: eventListForSelect,
    eventId,
    currentUser,
    setTemStep,
    setOpenModalConfirm,
    openModalConfirm,
    temStep,
    step,
    setStep,
    eventSelected,
    handleSetEventSelected,
    infoInterview,
    eventSelectedConsultant,
    setEventSelectedConsultant,
    answerCurrent,
    loading,
    selectedEventOptionValue,
  };
};
